import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import Card from '../components/UI/Card';
import StatCard from '../components/UI/StatCard';
import Badge from '../components/UI/Badge';
import Button from '../components/UI/Button';
import Table from '../components/UI/Table';
import { Input } from '../components/UI/input';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '../components/UI/select';
import { PageTransition, StaggerContainer, AnimatedItem, Spinner } from '@/components/motion';
import formatMoney from '@/lib/formatMoney';
import { ordersAPI } from '../services/api';

const ACTIVE_STATUSES = ['new', 'accepted', 'pending_new', 'partially_filled'];
const CANCELED_STATUSES = ['canceled', 'rejected', 'expired'];
const COLORS = ['#22c55e', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

const tooltipStyle = {
  backgroundColor: '#18181b',
  border: '1px solid hsl(var(--border))',
};

const getStatusBadge = (status) => {
  const statusMap = {
    new: 'info',
    accepted: 'info',
    pending_new: 'info',
    partially_filled: 'warning',
    filled: 'success',
    canceling: 'warning',
    canceled: 'danger',
    expired: 'danger',
    rejected: 'danger',
    replaced: 'neutral',
  };
  return <Badge variant={statusMap[status] || 'neutral'}>{status?.replace('_', ' ').toUpperCase()}</Badge>;
};

const getOrderTypeBadge = (type) => {
  const typeMap = {
    limit: { variant: 'info', label: 'LIMIT' },
    market: { variant: 'primary', label: 'MARKET' },
    stop: { variant: 'warning', label: 'STOP' },
    stop_limit: { variant: 'warning', label: 'STOP LMT' },
  };
  const config = typeMap[type] || { variant: 'neutral', label: type?.toUpperCase() };
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

function TradeHistory() {
  const queryClient = useQueryClient();
  const [symbolSearch, setSymbolSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sideFilter, setSideFilter] = useState('all');
  const [brokerFilter, setBrokerFilter] = useState('all');
  const [cancelingIds, setCancelingIds] = useState(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['orderHistory'],
    queryFn: () => ordersAPI.getHistory({ status: 'all', limit: 500 }),
    refetchInterval: 30000,
  });

  const orders = data?.data || [];
  const brokers = data?.brokers || [];

  const cancelMutation = useMutation({
    mutationFn: async (orderId) => {
      setCancelingIds((prev) => new Set(prev).add(orderId));
      return ordersAPI.cancel(orderId);
    },
    onSuccess: (_data, orderId) => {
      setCancelingIds((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      toast.success('Order canceled successfully');
      queryClient.invalidateQueries({ queryKey: ['orderHistory'] });
    },
    onError: (err, orderId) => {
      setCancelingIds((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      toast.error(`Cancel failed: ${err.message}`);
    },
  });

  // Stats
  const stats = useMemo(() => {
    const filled = orders.filter((o) => o.status === 'filled').length;
    const active = orders.filter((o) => ACTIVE_STATUSES.includes(o.status)).length;
    const canceled = orders.filter((o) => CANCELED_STATUSES.includes(o.status)).length;
    return { total: orders.length, filled, active, canceled };
  }, [orders]);

  // Chart data
  const statusChartData = useMemo(() => {
    const entries = [
      { name: 'Filled', value: stats.filled },
      { name: 'Active', value: stats.active },
      { name: 'Canceled', value: stats.canceled },
    ].filter((d) => d.value > 0);
    return entries;
  }, [stats]);

  const brokerChartData = useMemo(() => {
    const map = {};
    orders.forEach((o) => {
      map[o.broker] = (map[o.broker] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [orders]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (symbolSearch && !o.symbol?.toLowerCase().includes(symbolSearch.toLowerCase())) return false;
      if (statusFilter === 'active' && !ACTIVE_STATUSES.includes(o.status)) return false;
      if (statusFilter === 'filled' && o.status !== 'filled') return false;
      if (statusFilter === 'canceled' && !CANCELED_STATUSES.includes(o.status)) return false;
      if (sideFilter !== 'all' && o.side !== sideFilter) return false;
      if (brokerFilter !== 'all' && o.broker !== brokerFilter) return false;
      return true;
    });
  }, [orders, symbolSearch, statusFilter, sideFilter, brokerFilter]);

  const hasFilters = symbolSearch || statusFilter !== 'all' || sideFilter !== 'all' || brokerFilter !== 'all';

  const clearFilters = () => {
    setSymbolSearch('');
    setStatusFilter('all');
    setSideFilter('all');
    setBrokerFilter('all');
  };

  const columns = [
    {
      key: 'symbol',
      label: 'Symbol',
      render: (symbol) => <span className="font-semibold text-foreground">{symbol}</span>,
    },
    {
      key: 'side',
      label: 'Side',
      render: (side) => (
        <Badge variant={side === 'buy' ? 'success' : 'danger'}>{side?.toUpperCase()}</Badge>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (type) => getOrderTypeBadge(type),
    },
    {
      key: 'quantity',
      label: 'Qty',
      render: (qty) => parseFloat(qty).toFixed(2),
    },
    {
      key: 'filledPrice',
      label: 'Price',
      render: (filledPrice, row) => {
        const price = filledPrice || row.price;
        return price != null ? formatMoney(price) : '\u2014';
      },
    },
    {
      key: 'filledQty',
      label: 'Filled',
      render: (filledQty, row) =>
        `${parseFloat(filledQty || 0).toFixed(2)} / ${parseFloat(row.quantity).toFixed(2)}`,
    },
    {
      key: 'status',
      label: 'Status',
      render: (status) => getStatusBadge(status),
    },
    {
      key: 'broker',
      label: 'Broker',
      render: (broker) => <Badge variant="neutral">{broker}</Badge>,
    },
    {
      key: 'createdAt',
      label: 'Date',
      render: (date) => (date ? new Date(date).toLocaleString() : '\u2014'),
    },
    {
      key: '_actions',
      label: '',
      render: (_, row) => {
        if (!ACTIVE_STATUSES.includes(row.status)) return null;
        const isCanceling = cancelingIds.has(row.id);
        return (
          <Button
            size="sm"
            variant="danger"
            disabled={isCanceling}
            onClick={(e) => {
              e.stopPropagation();
              cancelMutation.mutate(row.id);
            }}
          >
            {isCanceling ? 'Canceling...' : 'Cancel'}
          </Button>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner className="h-16 w-16 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading order history...</p>
        </div>
      </div>
    );
  }

  if (brokers.length === 0 && orders.length === 0) {
    return (
      <PageTransition className="p-8">
        <h1 className="text-3xl font-bold text-foreground mb-6">Order History</h1>
        <Card>
          <div className="text-center py-16">
            <p className="text-lg text-muted-foreground mb-4">No broker connections found</p>
            <p className="text-sm text-muted-foreground/70 mb-6">
              Connect a broker in Settings to see your order history.
            </p>
            <Link to="/home/settings">
              <Button>Go to Settings</Button>
            </Link>
          </div>
        </Card>
      </PageTransition>
    );
  }

  return (
    <PageTransition className="p-8 space-y-6">
      {/* Stats Row */}
      <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <AnimatedItem>
          <StatCard title="Total Orders" value={stats.total} subtitle="Across all brokers" />
        </AnimatedItem>
        <AnimatedItem>
          <StatCard title="Filled" value={stats.filled} subtitle="Completed orders" />
        </AnimatedItem>
        <AnimatedItem>
          <StatCard title="Active" value={stats.active} subtitle="Open orders" />
        </AnimatedItem>
        <AnimatedItem>
          <StatCard title="Canceled" value={stats.canceled} subtitle="Canceled / rejected / expired" />
        </AnimatedItem>
      </StaggerContainer>

      {/* Charts Row */}
      {orders.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Orders by Status">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusChartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Orders by Broker">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={brokerChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {brokerChartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {/* Filter Bar */}
      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder="Search symbol..."
            value={symbolSearch}
            onChange={(e) => setSymbolSearch(e.target.value)}
            className="w-48"
          />

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="filled">Filled</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sideFilter} onValueChange={setSideFilter}>
            <SelectTrigger className="w-28">
              <SelectValue placeholder="Side" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sides</SelectItem>
              <SelectItem value="buy">Buy</SelectItem>
              <SelectItem value="sell">Sell</SelectItem>
            </SelectContent>
          </Select>

          <Select value={brokerFilter} onValueChange={setBrokerFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Broker" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Brokers</SelectItem>
              {brokers.map((b) => (
                <SelectItem key={b.id} value={b.name}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      </Card>

      {/* Orders Table */}
      <Card
        title="Orders"
        subtitle={`${filteredOrders.length} order${filteredOrders.length !== 1 ? 's' : ''}`}
      >
        {filteredOrders.length > 0 ? (
          <Table columns={columns} data={filteredOrders} />
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No orders found</p>
            {hasFilters ? (
              <Button variant="ghost" onClick={clearFilters}>
                Clear Filters
              </Button>
            ) : (
              <Link to="/home/strategies/builder">
                <Button>Go to Strategy Builder</Button>
              </Link>
            )}
          </div>
        )}
      </Card>
    </PageTransition>
  );
}

export default TradeHistory;
